"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import type { IPagination } from "@/lib/shared/types/pagination";
import { Button, HStack } from "@/ui";

interface BlogPaginationProps {
  pagination: IPagination;
  onPageChange: (page: number) => void;
}

/**
 * BlogPagination component - Pagination controls for blog list
 */
export const BlogPagination = ({ pagination, onPageChange }: BlogPaginationProps) => {
  const { page, hasNextPage, hasPrevPage, nextPage, prevPage, totalPages } = pagination;

  if (!page) return null;

  const renderPageButtons = () => {
    const buttons = [];

    // First page
    if (page > 1) {
      buttons.push(
        <Button
          key="first"
          onClick={() => handlePageChange(1)}
        >
          1
        </Button>,
      );

      // Ellipsis after first page if needed
      if (page > 3) {
        buttons.push(
          <Button
            key="ellipsis-start"
            variant="ghost"
          >
            ...
          </Button>,
        );
      }
    }

    // Previous page
    if (hasPrevPage && prevPage && prevPage !== 1) {
      buttons.push(
        <Button
          key="prev"
          onClick={() => handlePageChange(prevPage)}
        >
          {prevPage}
        </Button>,
      );
    }

    // Current page
    buttons.push(
      <Button
        key="current"
        variant="default"
      >
        {page}
      </Button>,
    );

    // Next page
    if (hasNextPage && nextPage && nextPage !== totalPages) {
      buttons.push(
        <Button
          key="next"
          onClick={() => handlePageChange(nextPage)}
        >
          {nextPage}
        </Button>,
      );
    }

    // Ellipsis before last page if needed
    if (page < totalPages - 2) {
      buttons.push(
        <Button
          key="ellipsis-end"
          variant="ghost"
        >
          ...
        </Button>,
      );
    }

    // Last page
    if (page < totalPages) {
      buttons.push(
        <Button
          key="last"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Button>,
      );
    }

    return buttons;
  };

  const handlePageChange = (page: number) => {
    onPageChange(page);
    // Scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <HStack className="justify-center gap-2">
      <Button
        disabled={!hasPrevPage}
        onClick={() => handlePageChange(prevPage!)}
        className="disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      {renderPageButtons()}
      <Button
        disabled={!hasNextPage}
        onClick={() => handlePageChange(nextPage!)}
        className="disabled:opacity-50"
      >
        <ArrowRight className="h-4 w-4" />
      </Button>
    </HStack>
  );
};
